import React, { useState } from "react";
import {
  CheckCircle,
  Mail,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Target,
  Heart,
  Compass,
  Shield,
  Lightbulb,
  Trophy,
  Mountain,
  Users,
  Battery,
  Flag,
  Eye,
  Calendar,
  ArrowRight,
} from "lucide-react";

const SuccessScreen = ({ firstName, email, fullReport, onStartNew }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionName) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  // Function to convert URLs to clickable links
  const makeLinksClickable = (str) => {
    if (typeof str !== "string") return str;
    const urlRegex = /(https?:\/\/[^\s)]+)/g;
    const parts = str.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        const cleanUrl = part.replace(/[.,;:!?)]+$/, "");
        const punctuation = part.slice(cleanUrl.length);

        return (
          <React.Fragment key={index}>
            <a
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-adhd-yellow hover:text-adhd-orange underline font-medium transition-colors break-all"
            >
              {cleanUrl}
            </a>
            {punctuation}
          </React.Fragment>
        );
      }
      return part;
    });
  };

  // Parse inline formatting (bold, italics, links)
  const parseInlineFormatting = (text) => {
    if (!text || typeof text !== "string") return text;

    let result = [];
    let remaining = text;
    let keyCounter = 0;

    while (remaining.length > 0) {
      // Check for bold (**text**)
      const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
      if (boldMatch) {
        result.push(
          <strong key={keyCounter++} className="font-bold text-adhd-yellow">
            {makeLinksClickable(boldMatch[1])}
          </strong>
        );
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }

      // Check for italic (*text*)
      const italicMatch = remaining.match(/^\*([^*\s][^*]*[^*\s])\*/);
      if (italicMatch && !remaining.startsWith("**")) {
        result.push(
          <em key={keyCounter++} className="italic text-gray-200">
            {makeLinksClickable(italicMatch[1])}
          </em>
        );
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }

      // Check for emoji patterns
      const emojiMatch = remaining.match(
        /^([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|✓|✅|→|💛)/u
      );
      if (emojiMatch) {
        result.push(
          <span key={keyCounter++} className="mr-1">
            {emojiMatch[1]}
          </span>
        );
        remaining = remaining.slice(emojiMatch[0].length);
        continue;
      }

      const nextSpecial = remaining.search(/\*|https?:\/\//);
      if (nextSpecial === -1) {
        result.push(...makeLinksClickable(remaining));
        break;
      } else if (nextSpecial === 0) {
        result.push(remaining[0]);
        remaining = remaining.slice(1);
      } else {
        result.push(...makeLinksClickable(remaining.slice(0, nextSpecial)));
        remaining = remaining.slice(nextSpecial);
      }
    }

    return result.length > 0 ? result : text;
  };

  // Enhanced content parser
  const parseReportContent = (text) => {
    if (!text) return null;

    const lines = text.split("\n");
    const elements = [];
    let currentList = [];
    let currentListType = null;
    let paragraphBuffer = [];
    let keyCounter = 0;

    const flushParagraph = () => {
      if (paragraphBuffer.length > 0) {
        const content = paragraphBuffer.join(" ").trim();
        if (content) {
          elements.push(
            <p
              key={`p-${keyCounter++}`}
              className="text-gray-300 leading-relaxed mb-4"
            >
              {parseInlineFormatting(content)}
            </p>
          );
        }
        paragraphBuffer = [];
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        if (currentListType === "numbered") {
          elements.push(
            <div key={`list-${keyCounter++}`} className="space-y-3 my-4">
              {currentList.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-adhd-yellow/20 to-adhd-orange/20 border border-adhd-yellow/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-adhd-yellow">
                      {item.number}
                    </span>
                  </div>
                  <p className="text-gray-300 leading-relaxed flex-1">
                    {parseInlineFormatting(item.text)}
                  </p>
                </div>
              ))}
            </div>
          );
        } else if (currentListType === "bullet") {
          elements.push(
            <ul key={`list-${keyCounter++}`} className="space-y-2 my-4">
              {currentList.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-adhd-yellow mt-1.5 text-xs">●</span>
                  <span className="text-gray-300 leading-relaxed flex-1">
                    {parseInlineFormatting(item.text)}
                  </span>
                </li>
              ))}
            </ul>
          );
        } else if (currentListType === "checklist") {
          elements.push(
            <ul key={`list-${keyCounter++}`} className="space-y-2 my-4">
              {currentList.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-adhd-green mt-0.5">✓</span>
                  <span className="text-gray-300 leading-relaxed flex-1">
                    {parseInlineFormatting(item.text)}
                  </span>
                </li>
              ))}
            </ul>
          );
        }
        currentList = [];
        currentListType = null;
      }
    };

    lines.forEach((line) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        flushParagraph();
        flushList();
        return;
      }

      // Handle horizontal rules
      if (/^[-_*]{3,}$/.test(trimmedLine)) {
        flushParagraph();
        flushList();
        elements.push(
          <hr
            key={`hr-${keyCounter++}`}
            className="border-adhd-yellow/20 my-6"
          />
        );
        return;
      }

      // Handle === style headers
      if (/^={3,}$/.test(trimmedLine)) {
        flushParagraph();
        flushList();
        elements.push(
          <hr
            key={`hr-${keyCounter++}`}
            className="border-adhd-yellow/30 my-8 border-2"
          />
        );
        return;
      }

      // Handle markdown headers
      if (trimmedLine.startsWith("####")) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^####\s*/, "").trim();
        elements.push(
          <h5
            key={`h4-${keyCounter++}`}
            className="text-base font-bold text-gray-200 mt-5 mb-2"
          >
            {parseInlineFormatting(headerText)}
          </h5>
        );
        return;
      }

      if (trimmedLine.startsWith("###")) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^###\s*/, "").trim();
        elements.push(
          <h4
            key={`h3-${keyCounter++}`}
            className="text-lg font-bold text-gray-100 mt-6 mb-3 flex items-center gap-2"
          >
            <span className="w-1.5 h-1.5 bg-adhd-yellow rounded-full"></span>
            {parseInlineFormatting(headerText)}
          </h4>
        );
        return;
      }

      if (trimmedLine.startsWith("##")) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^##\s*/, "").trim();
        elements.push(
          <h3
            key={`h2-${keyCounter++}`}
            className="text-xl font-bold text-adhd-yellow mt-8 mb-4 pb-2 border-b border-adhd-yellow/20"
          >
            {parseInlineFormatting(headerText)}
          </h3>
        );
        return;
      }

      if (trimmedLine.startsWith("#") && !trimmedLine.startsWith("##")) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^#\s*/, "").trim();
        elements.push(
          <h2
            key={`h1-${keyCounter++}`}
            className="text-2xl md:text-3xl font-black gradient-text mt-8 mb-6"
          >
            {parseInlineFormatting(headerText)}
          </h2>
        );
        return;
      }

      // Handle standalone bold headers
      if (
        trimmedLine.startsWith("**") &&
        trimmedLine.endsWith("**") &&
        trimmedLine.indexOf("**", 2) === trimmedLine.length - 2
      ) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.slice(2, -2).trim();
        elements.push(
          <h4
            key={`boldheader-${keyCounter++}`}
            className="text-lg font-bold text-adhd-yellow mt-6 mb-3"
          >
            {headerText}
          </h4>
        );
        return;
      }

      // Handle checklist items
      if (trimmedLine.startsWith("✓") || trimmedLine.startsWith("✅")) {
        flushParagraph();
        if (currentListType !== "checklist") {
          flushList();
          currentListType = "checklist";
        }
        const itemText = trimmedLine.replace(/^[✓✅]\s*/, "").trim();
        if (itemText) {
          currentList.push({ text: itemText });
        }
        return;
      }

      // Handle numbered lists
      const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)/);
      if (numberedMatch) {
        flushParagraph();
        if (currentListType !== "numbered") {
          flushList();
          currentListType = "numbered";
        }
        currentList.push({
          number: numberedMatch[1],
          text: numberedMatch[2].trim(),
        });
        return;
      }

      // Handle bullet points
      const bulletMatch = trimmedLine.match(/^[•\-*●]\s+(.+)/);
      if (bulletMatch) {
        flushParagraph();
        if (currentListType !== "bullet") {
          flushList();
          currentListType = "bullet";
        }
        currentList.push({ text: bulletMatch[1].trim() });
        return;
      }

      // Handle blockquotes
      if (trimmedLine.startsWith(">") || trimmedLine.startsWith('"')) {
        flushParagraph();
        flushList();
        const quoteText = trimmedLine
          .replace(/^[>"]\s*/, "")
          .replace(/"$/, "")
          .trim();
        elements.push(
          <blockquote
            key={`quote-${keyCounter++}`}
            className="border-l-4 border-adhd-yellow/50 pl-4 py-2 my-4 bg-adhd-yellow/5 rounded-r-lg"
          >
            <p className="text-gray-200 italic">
              {parseInlineFormatting(quoteText)}
            </p>
          </blockquote>
        );
        return;
      }

      paragraphBuffer.push(trimmedLine);
    });

    flushParagraph();
    flushList();

    return elements;
  };

  // Parse the report into main sections
  const parseReport = (reportContent) => {
    if (!reportContent) return [];

    const sections = [];
    const lines = reportContent.split("\n");
    let currentSection = null;
    let currentContent = [];

    lines.forEach((line) => {
      const trimmedLine = line.trim();

      const h2Match = trimmedLine.match(/^##\s+(.+)/);
      const boldHeaderMatch =
        trimmedLine.match(/^\*\*([A-Z][^*]+)\*\*$/) ||
        trimmedLine.match(/^\*\*([A-Z]{2,}[^*]+)\*\*$/);

      if (h2Match || boldHeaderMatch) {
        if (currentSection) {
          sections.push({
            title: currentSection,
            content: currentContent.join("\n").trim(),
          });
        }
        currentSection = h2Match
          ? h2Match[1].trim()
          : boldHeaderMatch[1].trim();
        currentContent = [];
      } else {
        currentContent.push(line);
      }
    });

    if (currentSection) {
      sections.push({
        title: currentSection,
        content: currentContent.join("\n").trim(),
      });
    }

    if (sections.length === 0) {
      sections.push({
        title: "Your Year-End Reflection Report",
        content: reportContent,
      });
    }

    return sections;
  };

  // Get appropriate icon for section
  const getSectionIcon = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("year in one") || lowerTitle.includes("overview"))
      return Eye;
    if (lowerTitle.includes("growth") || lowerTitle.includes("revealed"))
      return Sparkles;
    if (lowerTitle.includes("success") || lowerTitle.includes("win"))
      return Trophy;
    if (lowerTitle.includes("lesson") || lowerTitle.includes("challenge"))
      return Mountain;
    if (lowerTitle.includes("energy") || lowerTitle.includes("relationship"))
      return Users;
    if (lowerTitle.includes("vision") || lowerTitle.includes("ahead"))
      return Target;
    if (lowerTitle.includes("identity") || lowerTitle.includes("becoming"))
      return Heart;
    if (lowerTitle.includes("action") || lowerTitle.includes("focus"))
      return Flag;
    if (lowerTitle.includes("support") || lowerTitle.includes("boundaries"))
      return Shield;
    if (lowerTitle.includes("commitment") || lowerTitle.includes("integration"))
      return Battery;
    if (lowerTitle.includes("pattern") || lowerTitle.includes("insight"))
      return Lightbulb;
    if (lowerTitle.includes("q1") || lowerTitle.includes("action list"))
      return Calendar;
    if (lowerTitle.includes("question") || lowerTitle.includes("carry"))
      return Compass;
    if (lowerTitle.includes("part 1")) return Eye;
    if (lowerTitle.includes("part 2")) return Target;
    return Sparkles;
  };

  const reportSections = fullReport
    ? parseReport(fullReport.reportContent)
    : [];

  // Get current date for the report
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen gradient-bg py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="glass rounded-3xl shadow-2xl p-8 md:p-12 animate-fadeIn mb-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-adhd-green/20 to-adhd-green/10 border border-adhd-green/30 rounded-full flex items-center justify-center animate-scaleIn">
              <CheckCircle className="w-10 h-10 text-adhd-green" />
            </div>

            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="gradient-text">Well done, {firstName}!</span>
            </h1>

            <p className="text-xl text-gray-300 mb-2">
              Your Year-End Reflection Report is ready.
            </p>
            <p className="text-sm text-gray-500">Generated on {currentDate}</p>
          </div>

          {/* Email Notification */}
          <div className="glass-light rounded-2xl p-6 border border-adhd-yellow/10">
            <div className="flex items-start gap-4">
              <div className="p-3 glass rounded-xl">
                <Mail className="w-6 h-6 text-adhd-yellow" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-100 mb-2">
                  A copy has been sent to your email
                </h3>
                <p className="text-gray-300 mb-2">Check your inbox at:</p>
                <p className="font-bold text-adhd-yellow bg-gradient-to-r from-adhd-yellow/10 to-adhd-orange/10 border border-adhd-yellow/30 px-4 py-2 rounded-lg inline-block">
                  {email}
                </p>
                <p className="text-sm text-gray-400 mt-3">
                  Check your spam folder if you don't see it within 15 minutes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass rounded-xl p-4 border border-adhd-yellow/10 text-center">
            <div className="text-3xl font-black text-adhd-yellow">30</div>
            <div className="text-sm text-gray-400">Questions Answered</div>
          </div>
          <div className="glass rounded-xl p-4 border border-adhd-yellow/10 text-center">
            <div className="text-3xl font-black text-adhd-yellow">10</div>
            <div className="text-sm text-gray-400">Sections Completed</div>
          </div>
          <div className="glass rounded-xl p-4 border border-adhd-yellow/10 text-center">
            <div className="text-3xl font-black text-adhd-yellow">5</div>
            <div className="text-sm text-gray-400">Key Insights</div>
          </div>
          <div className="glass rounded-xl p-4 border border-adhd-yellow/10 text-center">
            <div className="text-3xl font-black text-adhd-yellow">Q1</div>
            <div className="text-sm text-gray-400">Action Plan</div>
          </div>
        </div>

        {/* Full Report Display */}
        {fullReport && (
          <div className="glass rounded-3xl shadow-2xl p-8 md:p-12 animate-slideUp">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-adhd-yellow/10 to-adhd-orange/10 border border-adhd-yellow/30 rounded-full text-adhd-yellow text-sm font-semibold mb-4">
                <Sparkles size={16} />
                <span>Your Personal Report</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-100 mb-4">
                Your Year-End Reflection
              </h2>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                Based on your 30 responses, we've synthesized your year and
                created a clear roadmap for the year ahead.
              </p>
            </div>

            {/* Report Sections */}
            <div className="space-y-4">
              {reportSections.map((section, index) => {
                const isExpanded = expandedSections[section.title] !== false;
                const SectionIcon = getSectionIcon(section.title);

                return (
                  <div
                    key={index}
                    className="glass-light rounded-2xl shadow-lg overflow-hidden border border-adhd-yellow/10 transition-all hover:border-adhd-yellow/20"
                  >
                    <button
                      onClick={() => toggleSection(section.title)}
                      className="w-full px-6 py-4 bg-gradient-to-r from-adhd-yellow/5 to-adhd-orange/5 hover:from-adhd-yellow/10 hover:to-adhd-orange/10 transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 glass border border-adhd-yellow/20 rounded-lg">
                          <SectionIcon size={20} className="text-adhd-yellow" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-100 text-left">
                          {section.title}
                        </h3>
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={20} className="text-gray-400" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="px-6 py-6 border-t border-adhd-yellow/10">
                        <div className="prose prose-invert max-w-none">
                          {parseReportContent(section.content)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Revisit Reminder */}
            <div className="mt-12 p-6 md:p-8 bg-gradient-to-br from-adhd-yellow/10 to-adhd-orange/5 border border-adhd-yellow/20 rounded-2xl">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-adhd-yellow/20 to-adhd-orange/20 border border-adhd-yellow/30 rounded-xl flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-adhd-yellow" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-bold text-gray-100 mb-2">
                    Bookmark This Report
                  </h3>
                  <p className="text-gray-300">
                    Save this page or check your email for a copy. Revisit it
                    monthly to track your progress, especially when you need to
                    remember what you committed to. Consider scheduling a Q1
                    review in your calendar for
                    <strong className="text-adhd-yellow"> March 31st</strong>.
                  </p>
                </div>
              </div>
            </div>

            {/* Closing */}
            <div className="mt-8 text-center">
              <p className="text-gray-400 text-sm">
                Here's to making next year count. 💛
              </p>
            </div>
          </div>
        )}

        {/* Start New Assessment */}
        {onStartNew && (
          <div className="mt-8 text-center">
            <button
              onClick={onStartNew}
              className="inline-flex items-center gap-2 px-6 py-3 glass hover:bg-adhd-yellow/10 text-gray-300 rounded-xl transition-all border border-adhd-yellow/20 hover:border-adhd-yellow/40"
            >
              <ArrowRight size={18} />
              <span>Start a New Reflection</span>
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            This report was generated based on your 30 reflection responses.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuccessScreen;
